/**
 * Head yaw/pitch from MediaPipe Face Landmarker facialTransformationMatrixes.
 * Uses Three.js Euler YXZ (same stack already in the app) for a stable convention.
 */

import { Euler, Matrix4 } from 'three';
import type { HeadPoseDegrees } from './headPoseFromLandmarks';

export {
  HEAD_PITCH_AWAY_DEG,
  HEAD_YAW_AWAY_DEG,
  isHeadPoseAwayFromCamera,
  type HeadPoseDegrees,
} from './headPoseFromLandmarks';

const _mat = new Matrix4();
const _euler = new Euler();

/**
 * Extract yaw (Y) and pitch (X) in degrees from a column-major 4×4 matrix.
 */
export function yawPitchFromFacialMatrix(
  matrix: ArrayLike<number> | null | undefined
): HeadPoseDegrees | null {
  if (!matrix || matrix.length < 16) return null;

  const arr =
    matrix instanceof Float32Array
      ? matrix
      : Float32Array.from(matrix as ArrayLike<number>);

  _mat.fromArray(arr);
  _euler.setFromRotationMatrix(_mat, 'YXZ');

  const yaw = (_euler.y * 180) / Math.PI;
  const pitch = (_euler.x * 180) / Math.PI;

  if (!Number.isFinite(yaw) || !Number.isFinite(pitch)) return null;
  return { yaw, pitch };
}

/**
 * Fallback pose from Face Landmarker normalized landmarks (478 points).
 * Used when the transformation matrix is missing or near-identity incorrectly.
 */
export function yawPitchFromNormalizedLandmarks(
  landmarks: ReadonlyArray<{ x: number; y: number; z?: number }> | null | undefined
): HeadPoseDegrees | null {
  if (!landmarks || landmarks.length < 455) return null;
  const nose = landmarks[1];
  const leftCheek = landmarks[234];
  const rightCheek = landmarks[454];
  const forehead = landmarks[10];
  const chin = landmarks[152];
  if (!nose || !leftCheek || !rightCheek || !forehead || !chin) return null;

  const midX = (leftCheek.x + rightCheek.x) / 2;
  const faceWidth = Math.hypot(rightCheek.x - leftCheek.x, rightCheek.y - leftCheek.y) || 1e-6;
  const yaw =
    Math.asin(
      Math.max(-1, Math.min(1, (nose.x - midX) / (faceWidth * 0.45)))
    ) *
    (180 / Math.PI);

  // Cheek depth (normalized z) — amplify small z into face-width units
  const zL = leftCheek.z ?? 0;
  const zR = rightCheek.z ?? 0;
  const zSpan = Math.abs(zL - zR);
  const zScale = zSpan > 0.02 ? 1 : 1 / Math.max(zSpan, 1e-4);
  const yawZ = Math.atan2((zL - zR) * zScale, 1) * (180 / Math.PI);

  const leftOuter = landmarks[33];
  const leftInner = landmarks[133];
  const rightOuter = landmarks[263];
  const rightInner = landmarks[362];
  let yawEyes = 0;
  let profile = false;
  if (leftOuter && rightOuter && leftInner && rightInner) {
    const leftW = Math.hypot(leftOuter.x - leftInner.x, leftOuter.y - leftInner.y);
    const rightW = Math.hypot(rightOuter.x - rightInner.x, rightOuter.y - rightInner.y);
    const narrow = Math.min(leftW, rightW);
    const wide = Math.max(leftW, rightW);
    if (wide > 1e-4 && narrow / wide < 0.5) {
      profile = true;
      yawEyes = (leftW < rightW ? -1 : 1) * 35;
    }
  }

  const midY = (forehead.y + chin.y) / 2;
  const faceHeight = Math.hypot(chin.x - forehead.x, chin.y - forehead.y) || 1e-6;
  const pitch =
    Math.asin(
      Math.max(-1, Math.min(1, (nose.y - midY) / (faceHeight * 0.45)))
    ) *
    (180 / Math.PI);

  const combinedYaw = [yaw, yawZ * 0.5, yawEyes].reduce((a, b) =>
    Math.abs(b) > Math.abs(a) ? b : a
  );
  return {
    yaw: profile ? (Math.abs(combinedYaw) < 22 ? yawEyes : combinedYaw) : combinedYaw,
    pitch,
    profileByEyes: profile,
  };
}
